import { useEffect, useState } from "react";
import { FolderGit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { shortPath } from "@/components/project-select";
import { api, ApiError } from "@/lib/api";
import { boardProjectIds } from "@/lib/board-projects";
import type { BoardDetail, Project } from "@/lib/types";
import { previewText } from "@/lib/preview-text";
import { cn } from "@/lib/utils";

/**
 * The projects a board covers, and which one (if any) is the default.
 *
 * Board-level rather than per-card is the common case by a distance: a board is
 * usually about one codebase, and setting it once means every card on it —
 * including the ones a paste or a chat import creates later — is already actionable.
 * A product split across repos is one board covering several, with each card
 * naming its own when there is no default.
 */
export function BoardProjectDialog({
  board,
  projects,
  open,
  onOpenChange,
  onSaved,
  onManageProjects,
}: {
  board: BoardDetail | null;
  projects: Project[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  onManageProjects: () => void;
}) {
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savedIds = boardProjectIds(board).join();
  useEffect(() => {
    if (!open || !board) return;
    setMemberIds(boardProjectIds(board));
    setDefaultId(board.board.projectId);
    setError(null);
  }, [open, board?.board.id, board?.board.projectId, savedIds]);

  const toggle = (projectId: string) => {
    if (memberIds.includes(projectId)) {
      setMemberIds(memberIds.filter((id) => id !== projectId));
      if (defaultId === projectId) setDefaultId(null);
    } else {
      setMemberIds([...memberIds, projectId]);
      // One project on its own is the default, as a board's single project always was.
      if (memberIds.length === 0) setDefaultId(projectId);
    }
  };
  const makeDefault = (projectId: string | null) => {
    setDefaultId(projectId);
    if (projectId && !memberIds.includes(projectId)) setMemberIds([...memberIds, projectId]);
  };

  // Cards naming each project themselves, so the user sees which repos the work is in.
  const named = new Map<string, number>();
  for (const task of board?.tasks ?? []) {
    if (task.projectId) named.set(task.projectId, (named.get(task.projectId) ?? 0) + 1);
  }
  const chosen = projects.find((project) => project.id === defaultId) ?? null;
  const inheriting = board?.tasks.filter((task) => task.projectId === null).length ?? 0;

  const submit = async () => {
    if (!board) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateBoard(board.board.id, { projects: memberIds, project: defaultId });
      onOpenChange(false);
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not set the projects");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open && board !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Projects for this board</DialogTitle>
          <DialogDescription>
            The directories work on {board ? `"${board.board.name}"` : "this board"} happens in. Tick every repo it
            covers; a card without its own project inherits the default, so an{" "}
            <span className="font-mono text-[11px]">@agent</span> request on it runs inside that codebase.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-1.5">
          <div className="flex items-baseline justify-between text-xs font-medium">
            <span>Directories</span>
            <span className="text-muted-foreground">Default</span>
          </div>
          <ul className="max-h-[min(18rem,40dvh)] divide-y divide-border/60 overflow-y-auto rounded-md border border-border/70">
            {projects.map((project) => {
              const member = memberIds.includes(project.id);
              const count = named.get(project.id) ?? 0;
              return (
                <li key={project.id} className={cn("flex items-center gap-2.5 px-2.5 py-2", member && "bg-primary/5")}>
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      checked={member}
                      onChange={() => toggle(project.id)}
                      aria-label={`Board covers ${project.name}`}
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-sm">
                        <FolderGit2 className="size-3.5 shrink-0 text-primary" />
                        <span className="truncate">{project.name}</span>
                      </span>
                      <span className="block truncate font-mono text-[10px] text-muted-foreground" title={project.hostPath}>
                        {shortPath(project.hostPath)}
                        {count > 0 ? (
                          <span className="font-sans">
                            {" "}
                            · {count} card{count > 1 ? "s" : ""} name it
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </label>
                  <input
                    type="radio"
                    name="board-default-project"
                    className="size-4 shrink-0 accent-primary"
                    checked={defaultId === project.id}
                    onChange={() => makeDefault(project.id)}
                    aria-label={`Make ${project.name} the default`}
                  />
                </li>
              );
            })}
            {projects.length === 0 ? (
              <li className="px-2.5 py-3 text-xs text-muted-foreground">No projects registered yet.</li>
            ) : null}
          </ul>
          {/* Outside the scrolling list, so "none" is always in reach on a long one. */}
          {projects.length > 0 ? (
            <div className="flex items-center gap-2.5 rounded-md border border-border/70 px-2.5 py-2">
              <label htmlFor="board-default-none" className="min-w-0 flex-1 cursor-pointer text-sm text-muted-foreground">
                No default — each card names its own
              </label>
              <input
                id="board-default-none"
                type="radio"
                name="board-default-project"
                className="size-4 shrink-0 accent-primary"
                checked={defaultId === null}
                onChange={() => makeDefault(null)}
              />
            </div>
          ) : null}
        </div>

        {chosen ? (
          <div className="rounded-md border border-primary/25 bg-primary/8 p-2.5 text-xs">
            <p className="flex min-w-0 items-center gap-2 font-mono text-[11px] text-foreground">
              <FolderGit2 className="size-3.5 shrink-0 text-primary" />
              <span className="min-w-0 truncate" title={chosen.hostPath}>{shortPath(chosen.hostPath)}</span>
            </p>
            {chosen.description ? (
              <p className="mt-1.5 line-clamp-3 break-words leading-snug text-muted-foreground">{previewText(chosen.description)}</p>
            ) : null}
            {/* Said plainly, because this is the moment it becomes true. */}
            <p className="mt-1.5 leading-snug text-muted-foreground">
              Claude can read and change files in there when it answers a request on a card that names no project of
              its own{inheriting > 0 ? ` (${inheriting} on this board now)` : ""}.
            </p>
          </div>
        ) : memberIds.length > 1 ? (
          <p className="text-xs text-muted-foreground">
            With no default, a new card starts with no project: pick one of these on the card, or an agent can only
            answer from the board.
          </p>
        ) : null}

        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
              onManageProjects();
            }}
          >
            Manage projects
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
